// ============================================================
// ANSICHTSSACHE-AGENT – Aufklappmenue
// ============================================================
// Das Menue haengt in einem PORTAL und nicht im Elternteil.
//
// Das ist der ganze Grund fuer diese Fassung. Vorher war es ein
// `absolute` positioniertes Feld direkt neben dem Ausloeser —
// und damit ein Kind der Karte, in der es steht. Jede Karte mit
// `overflow-hidden` (Rechnung, Angebot, Projektkosten) hat das
// Menue an ihrer Unterkante abgeschnitten: sichtbar war nur,
// was zufaellig noch auf die Karte passte. Kein Scrollen half,
// denn abgeschnitten wird beim Zeichnen, nicht beim Layouten.
//
// Ein Portal haengt den Inhalt unten an <body>. Damit kennt es
// keine Karte mehr, die es beschneiden koennte, und Radix legt
// zusaetzlich das Ausweichen am Bildschirmrand bei: ist unten
// kein Platz, klappt das Menue nach oben.
//
// Dasselbe Verfahren wie im `Steuerpanel` — dort ist es Popover,
// hier DropdownMenu, weil es hier um eine Liste von Befehlen
// geht. Das bringt Tastaturbedienung, Escape, Klick daneben und
// die Fokusrueckgabe mit, die vorher von Hand nachgebaut waren.
//
// Die Schnittstelle nach aussen ist unveraendert: `trigger`,
// `align`, und als Kinder `DropdownItem`, `DropdownLabel`,
// `DropdownSeparator`.
// ============================================================

"use client";

import * as Menu from "@radix-ui/react-dropdown-menu";
import { cn } from "@/lib/utils";

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
  /** Von aussen oeffnen — etwa mit der rechten Maustaste auf die Karte. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function DropdownMenu({
  trigger,
  children,
  align = "right",
  className,
  open,
  onOpenChange,
}: DropdownMenuProps) {
  return (
    <Menu.Root open={open} onOpenChange={onOpenChange}>
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>

      <Menu.Portal>
        <Menu.Content
          align={align === "right" ? "end" : "start"}
          sideOffset={6}
          // Abstand zum Bildschirmrand, damit das Menue nicht an
          // der Kante klebt, wenn es ausweichen muss.
          collisionPadding={12}
          className={cn(
            "border-border bg-bg-primary shadow-dropdown z-50 min-w-[180px] rounded-lg border py-1",
            "animate-fade-in",
            // Eine lange Liste — der Positions-Typenwaehler hat
            // zehn Eintraege — darf nie hoeher werden als der
            // Platz, der wirklich da ist.
            "max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto",
            className,
          )}
        >
          {children}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

interface DropdownItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "danger";
}

function DropdownItem({ children, className, variant = "default", ...props }: DropdownItemProps) {
  return (
    <Menu.Item asChild>
      <button
        type="button"
        className={cn(
          "text-body-sm flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left",
          "duration-fast transition-colors",
          "focus:outline-none",
          // Radix markiert den Eintrag unter Zeiger UND Tastatur
          // mit `data-highlighted` — eine Hervorhebung fuer beide
          // Bedienarten statt getrennter hover-/focus-Regeln.
          variant === "danger"
            ? "text-danger data-[highlighted]:bg-danger/5"
            : "text-text-primary data-[highlighted]:bg-bg-secondary",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    </Menu.Item>
  );
}

function DropdownSeparator() {
  return <Menu.Separator className="bg-border my-1 h-px" />;
}

function DropdownLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <Menu.Label className={cn("text-overline text-text-tertiary px-3 py-1.5", className)}>
      {children}
    </Menu.Label>
  );
}

export { DropdownMenu, DropdownItem, DropdownSeparator, DropdownLabel };
